import ArchiveIcon from '@atlaskit/icon/glyph/archive';
import BitbucketOutputIcon from '@atlaskit/icon/glyph/bitbucket/output';
import EmailIcon from '@atlaskit/icon/glyph/email';
import LocationIcon from '@atlaskit/icon/glyph/location';
import LockIcon from '@atlaskit/icon/glyph/lock';
import PageIcon from '@atlaskit/icon/glyph/page';
import TrayIcon from '@atlaskit/icon/glyph/tray';
import WatchIcon from '@atlaskit/icon/glyph/watch';
import WorldIcon from '@atlaskit/icon/glyph/world';
import React from 'react';
import { bugBountyProgramTransform, ccpaBusiness, ccpaServiceProvider, compliantCertifications, customRetentionPeriod, dataProcessingAgreement, dataResidencyInScopeEndUserData, dataResidencyOptions, dataResidencyRealmMigration, dataRetentionTransform, endUserDataLogQuestion, endUserDataSharedWithThirdPartyEntities, endUserDataStoresAndProcessAnswer, gdprDataController, gdprDataProcessor, gdprDataTransfer, integrationPermisions, logEndUserData, logEndUserDataOutsideAtlassian, logIntegralPartOfFunctionality, logSharedWithThirdParty, privacyEnhancingTechniques, privacyPolicy, securityCaiqLite, securityContact, securityDiskEncryption, securityPolicyLink } from './utils/TrustTransform';
import shieldIcon from './images/shield-icon.svg';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Single source of truth for the content related to Privacy & Security
 * Check './README.md' to know more.
 */
export const privacySecurityConfig = [{
  heading: 'Data storage and management',
  expandText: 'Expand all data storage and management details',
  qna: [{
    question: () => [{
      type: 'text',
      text: 'End-User Data processed and/or stored outside of Atlassian products and services (excluding process/storage of End-User Data in logs)'
    }, {
      type: 'info-label-with-tooltip',
      text: 'What is “End-User Data”?',
      content: [{
        type: 'text',
        text: '“End-User Data” is any data, content or information of an end user that is accessed, collected or otherwise processed by you or your app in connection with use of the Atlassian Marketplace.'
      }]
    }],
    answer: endUserDataStoresAndProcessAnswer,
    iconBefore: /*#__PURE__*/_jsx(TrayIcon, {
      label: ""
    })
  }, {
    question: () => [{
      type: 'text',
      text: 'Data residency for in-scope End-User Data'
    }, {
      type: 'info-label-with-tooltip',
      text: 'Learn more about Data residency',
      content: [{
        type: 'text',
        text: 'Data residency allows you to control where in-scope End User Data is hosted. It allows you to choose whether in-scope End-User Data is globally distributed or in a defined geographic location.'
      }]
    }],
    answer: dataResidencyOptions,
    iconBefore: /*#__PURE__*/_jsx(WorldIcon, {
      label: ""
    })
  }, {
    question: () => [{
      type: 'text',
      text: 'List of in-scope End-User Data'
    }, {
      type: 'info-label-with-tooltip',
      text: 'Learn more about in-scope End-User Data',
      content: [{
        type: 'text',
        text: 'In-scope End-User Data is data that can be pinned to a particular region, in accordance with an app’s data residency options.'
      }]
    }],
    answer: dataResidencyInScopeEndUserData,
    iconBefore: /*#__PURE__*/_jsx(LocationIcon, {
      label: ""
    })
  }, {
    question: () => [{
      type: 'text',
      text: 'Migration of in-scope End-User Data between data residency supported locations'
    }],
    answer: dataResidencyRealmMigration
  }, {
    question: () => [{
      type: 'text',
      text: 'End-User Data shared with third-party entities'
    }],
    answer: endUserDataSharedWithThirdPartyEntities,
    iconBefore: /*#__PURE__*/_jsx(BitbucketOutputIcon, {
      label: ""
    })
  }, {
    question: endUserDataLogQuestion,
    answer: logEndUserData
  }, {
    question: () => [{
      type: 'text',
      text: 'End-User Data processed and/or stored in logs outside of Atlassian products and services'
    }],
    answer: logEndUserDataOutsideAtlassian
  }, {
    question: () => [{
      type: 'text',
      text: 'End-User Data in logs shared with third-party entities'
    }],
    answer: logSharedWithThirdParty
  }, {
    question: () => [{
      type: 'text',
      text: 'End-User Data in logs shared with third party entities integral for app functionality'
    }],
    answer: logIntegralPartOfFunctionality
  }, {
    question: () => [{
      type: 'text',
      text: 'End-User Data stored after app is uninstalled'
    }],
    answer: dataRetentionTransform,
    iconBefore: /*#__PURE__*/_jsx(ArchiveIcon, {
      label: ""
    })
  }, {
    question: () => [{
      type: 'text',
      text: 'Custom retention period for End-User Data'
    }],
    answer: customRetentionPeriod
  }]
}, {
  heading: 'Security and compliance',
  expandText: 'Expand all security and compliance details',
  qna: [{
    question: () => [{
      type: 'text',
      text: 'Integration permissions with Atlassian products'
    }],
    answer: integrationPermisions,
    iconBefore: /*#__PURE__*/_jsx(WatchIcon, {
      label: ""
    })
  }, {
    question: () => [{
      type: 'text',
      text: 'Marketplace Security Bug Bounty Program participant'
    }, {
      type: 'link-with-icon',
      text: 'Learn more about this program',
      href: 'https://developer.atlassian.com/platform/marketplace/marketplace-security-bug-bounty-program/'
    }],
    answer: bugBountyProgramTransform
  }, {
    question: () => [{
      type: 'text',
      text: 'Compliance certifications'
    }],
    answer: compliantCertifications,
    iconBefore: /*#__PURE__*/_jsx(PageIcon, {
      label: ""
    })
  }, {
    question: () => [{
      type: 'text',
      text: 'Contact for app security issues'
    }],
    answer: securityContact,
    iconBefore: /*#__PURE__*/_jsx(EmailIcon, {
      label: ""
    })
  }, {
    question: () => [{
      type: 'text',
      text: 'CAIQ Lite'
    }, {
      type: 'link-with-icon',
      text: 'Learn more about CAIQ Lite',
      href: 'https://cloudsecurityalliance.org/star/caiq-lite/'
    }],
    answer: securityCaiqLite
  }, {
    question: () => [{
      type: 'text',
      text: 'Security policy'
    }],
    answer: securityPolicyLink,
    iconBefore: /*#__PURE__*/_jsx(LockIcon, {
      label: ""
    })
  }, {
    question: () => [{
      type: 'text',
      text: 'Full disk encryption for data stored outside of Atlassian'
    }, {
      type: 'info-label-with-tooltip',
      text: 'Learn more about security requirements for cloud applications',
      content: [{
        type: 'text',
        text: 'Atlassian maintains a list of security requirements for cloud applications. This requirement is mandated by Atlassian to ensure security across the Marketplace.'
      }, {
        type: 'link-with-icon',
        text: 'Read more about security requirements',
        href: 'https://developer.atlassian.com/platform/marketplace/security-requirements/'
      }]
    }],
    answer: securityDiskEncryption
  }]
}, {
  heading: 'Privacy',
  expandText: 'Expand all privacy details',
  qna: [{
    question: () => [{
      type: 'text',
      text: 'Privacy policy'
    }],
    answer: privacyPolicy,
    iconBefore: /*#__PURE__*/_jsx("img", {
      alt: "",
      src: shieldIcon
    })
  }, {
    question: () => [{
      type: 'text',
      text: "Company/Organization is a 'data controller' under the General Data Protection Regulation (GDPR) with reference to this app"
    }, {
      type: 'link-with-icon',
      text: "Learn more about 'data controllers' under the GDPR",
      href: 'https://ec.europa.eu/info/law/law-topic/data-protection/reform/rules-business-and-organisations/obligations/controller-processor/what-data-controller-or-data-processor_en'
    }],
    answer: gdprDataController
  }, {
    question: () => [{
      type: 'text',
      text: "Company/Organization is a 'data processor' under the General Data Protection Regulation (GDPR) with reference to this app"
    }, {
      type: 'link-with-icon',
      text: "Learn more about 'data processors' under the GDPR",
      href: 'https://ec.europa.eu/info/law/law-topic/data-protection/reform/rules-business-and-organisations/obligations/controller-processor/what-data-controller-or-data-processor_en'
    }],
    answer: gdprDataProcessor
  }, {
    question: () => [{
      type: 'text',
      text: "Company/Organization is a 'business' under the California Consumer Privacy Act of 2018 (CCPA) with reference to this app"
    }, {
      type: 'link-with-icon',
      text: "Learn more about 'businesses' under the CCPA",
      href: 'https://oag.ca.gov/privacy/ccpa'
    }],
    answer: ccpaBusiness
  }, {
    question: () => [{
      type: 'text',
      text: "Company/Organization is a 'service provider' under the California Consumer Privacy Act of 2018 (CCPA) with reference to this app"
    }, {
      type: 'link-with-icon',
      text: "Learn more about 'service providers' under the CCPA",
      href: 'https://oag.ca.gov/privacy/ccpa'
    }],
    answer: ccpaServiceProvider
  }, {
    question: () => [{
      type: 'text',
      text: "General Data Protection Regulation (GDPR) approved mechanism for transferring European Economic Area (EEA) residents\u2019s End-User Data outside of the EEA"
    }, {
      type: 'link-with-icon',
      text: 'Learn more about GDPR approved transfer mechanisms',
      href: 'https://ec.europa.eu/info/law/law-topic/data-protection/international-dimension-data-protection/rules-international-data-transfers_en'
    }],
    answer: gdprDataTransfer
  }, {
    question: () => [{
      type: 'text',
      text: 'Privacy enhancing technologies (PETs) used to protect End-User Data'
    }],
    answer: privacyEnhancingTechniques
  }, {
    question: () => [{
      type: 'text',
      text: 'Data Processing Agreement (DPA) for customers'
    }, {
      type: 'link-with-icon',
      text: 'Learn more about DPAs',
      href: 'https://gdpr.eu/what-is-data-processing-agreement/'
    }],
    answer: dataProcessingAgreement
  }]
}];
export default privacySecurityConfig;